/**
 * CITY HOP — top-down grid-locked lane-crossing hopper (MVP).
 *
 * Heritage: Frogger (lane crossing), Freeway (chicken-on-highway).
 * Original mechanic: 8-direction grid hops including diagonals. Diagonal
 * sidewalk tiles at intersections let the player cut from one road
 * group to the next across two lanes at once — faster, riskier.
 *
 * Architecture:
 *   - 24 col × 12 row tile grid, 20px tiles
 *   - Alternating bands top→bottom: S R R R S R R R S R R S
 *     (goal sidewalk on top, start sidewalk on bottom, three groups of
 *     three road lanes separated by sidewalk bands)
 *   - Player snaps to grid cells; hop input fires on justPressed edges
 *     of 8 directions
 *   - Cars travel in continuous-position floats along their lane; lane
 *     direction alternates so the player must scan both sides
 *   - Collision: tile-grid for player vs AABB for car at sub-tile precision
 *
 * Determinism: all randomness through ctx.random (PRNG). Same
 * (seed, inputFrames) → same final score.
 */

import type {
  GameContext,
  GameInstance,
  InputState,
} from '@arcraz/arcadia-types';
import {
  CAR_BUS,
  CAR_BUS_DARK,
  CAR_CIVILIAN,
  CAR_CIVILIAN_DARK,
  CAR_FIRE,
  CAR_FIRE_DARK,
  CAR_HEADLIGHT,
  CAR_TAXI,
  CAR_TAXI_DARK,
  CAR_VAN,
  CAR_VAN_DARK,
  CAR_WINDOW,
  CRASH_FLASH,
  HUD_LEVEL,
  HUD_LIVES,
  HUD_SCORE,
  HUD_TEXT,
  LEVEL_UP_FLASH,
  PLAYER_BODY,
  PLAYER_HEAD,
  PLAYER_HIT,
  PLAYER_LIMB,
  ROAD_DARK,
  ROAD_EDGE,
  ROAD_LIGHT,
  ROAD_LINE,
  SIDEWALK_DARK,
  SIDEWALK_DIAGONAL,
  SIDEWALK_LIGHT,
  SIDEWALK_LINE,
} from './palette.js';

const WIDTH = 480;
const HEIGHT = 270;
const HUD_H = 30;
const PLAYFIELD_TOP = HUD_H;
const PLAYFIELD_H = HEIGHT - HUD_H; // 240

const TILE = 20;
const COLS = WIDTH / TILE; // 24
const ROWS = PLAYFIELD_H / TILE; // 12

/**
 * Band layout (12 rows total, top to bottom):
 *   row 0:  S — goal sidewalk
 *   row 1:  R — lane (cars west → east)
 *   row 2:  R — lane (cars east → west)
 *   row 3:  R — lane (cars west → east)
 *   row 4:  S — refuge sidewalk
 *   row 5:  R — lane
 *   row 6:  R — lane
 *   row 7:  R — lane
 *   row 8:  S — refuge sidewalk
 *   row 9:  R — lane
 *   row 10: R — lane
 *   row 11: S — start sidewalk
 */
type BandType = 'sidewalk' | 'road';
const BAND_LAYOUT: ReadonlyArray<BandType> = [
  'sidewalk', // 0 — goal
  'road',     // 1
  'road',     // 2
  'road',     // 3
  'sidewalk', // 4 — refuge
  'road',     // 5
  'road',     // 6
  'road',     // 7
  'sidewalk', // 8 — refuge
  'road',     // 9
  'road',     // 10
  'sidewalk', // 11 — start
];
const START_ROW = 11;
const GOAL_ROW = 0;

// Lane direction. Alternating per road row so adjacent lanes never share
// direction — forces the player to scan both sides at every band.
function laneDirection(row: number): 1 | -1 {
  return row % 2 === 0 ? 1 : -1;
}

// Player physics.
const HOP_COOLDOWN_TICKS = 8; // ticks the player can't hop again
const FLASH_TICKS = 18;
const LEVEL_FLASH_TICKS = 30;
const STARTING_LIVES = 3;
const MAX_LEVEL = 8;

// Score.
const SCORE_PER_ROW = 10;
const SCORE_PER_LEVEL = 500;

// Cars.
const CAR_W = TILE - 2; // 18 — slight visual gap between cars
const CAR_H = TILE - 4; // 16
const CAR_BASE_SPEED = 0.6; // px/tick at level 1
const CAR_SPEED_VAR = 0.5;
const CAR_LEVEL_RAMP = 0.15; // +15% car speed per level

// Spawn cadence. We use a per-lane timer so each lane has its own rhythm.
const SPAWN_TIMER_MIN = 60;
const SPAWN_TIMER_MAX = 180;

type Phase = 'playing' | 'crashed' | 'level-up' | 'game-over';

interface Car {
  /** Top-left x in pixels. */
  x: number;
  /** Lane row index. */
  row: number;
  /** Pixel velocity (signed). */
  vx: number;
  bodyColor: string;
  bodyDark: string;
  /** Width in pixels (variable for buses / vans). */
  width: number;
}

interface CarKind {
  body: string;
  dark: string;
  /** Width multiplier (1 = standard, 2 = long bus). */
  widthMult: 1 | 2;
}

const CAR_KINDS: ReadonlyArray<CarKind> = [
  { body: CAR_TAXI, dark: CAR_TAXI_DARK, widthMult: 1 },
  { body: CAR_FIRE, dark: CAR_FIRE_DARK, widthMult: 1 },
  { body: CAR_CIVILIAN, dark: CAR_CIVILIAN_DARK, widthMult: 1 },
  { body: CAR_VAN, dark: CAR_VAN_DARK, widthMult: 1 },
  { body: CAR_BUS, dark: CAR_BUS_DARK, widthMult: 2 }, // long bus
];

export class CityHopInstance implements GameInstance {
  private readonly ctx: GameContext;

  // Player.
  private playerCol = Math.floor(COLS / 2);
  private playerRow = START_ROW;
  /**
   * Highest row reached this attempt (lowest row index). Used to score
   * "advancing" hops — backwards hops give 0 points.
   */
  private bestRow = START_ROW;
  private hopCooldown = 0;

  // Traffic. Keyed by row → array of cars.
  private cars: Map<number, Car[]> = new Map();
  private laneSpawnTimers: Map<number, number> = new Map();

  // Score + state.
  private score = 0;
  private level = 1;
  private lives = STARTING_LIVES;
  private phase: Phase = 'playing';
  private crashFlashLeft = 0;
  private levelFlashLeft = 0;
  private finalScore = 0;
  private finalLevel = 1;
  private restartArmed = false;

  constructor(ctx: GameContext) {
    this.ctx = ctx;
    this.initLanes();
  }

  // --------------------------------------------------------------------------
  // GameInstance contract
  // --------------------------------------------------------------------------

  input(state: InputState): void {
    if (this.phase === 'game-over') {
      if (state.justPressed.has('fireA')) this.restartArmed = true;
      return;
    }
    if (this.phase !== 'playing') return;
    if (this.hopCooldown > 0) return;

    // Compute 8-direction hop from justPressed combinations. Diagonals
    // are simultaneous press of two orthogonal keys.
    let dCol = 0;
    let dRow = 0;
    if (state.justPressed.has('left')) dCol -= 1;
    if (state.justPressed.has('right')) dCol += 1;
    if (state.justPressed.has('up')) dRow -= 1;
    if (state.justPressed.has('down')) dRow += 1;

    // Also support held-in-conjunction: if up was justPressed and left is
    // currently held, treat as NW. This is the case where the player
    // holds left first then taps up — we want the diagonal to register.
    if (state.justPressed.has('up')) {
      if (state.left) dCol -= 1;
      if (state.right) dCol += 1;
    } else if (state.justPressed.has('down')) {
      if (state.left) dCol -= 1;
      if (state.right) dCol += 1;
    } else if (state.justPressed.has('left') || state.justPressed.has('right')) {
      if (state.up) dRow -= 1;
      if (state.down) dRow += 1;
    }
    // Clamp to single-tile movement (the simultaneous code above can
    // double-add when both edges fire in one frame).
    if (dCol < -1) dCol = -1;
    if (dCol > 1) dCol = 1;
    if (dRow < -1) dRow = -1;
    if (dRow > 1) dRow = 1;

    if (dCol === 0 && dRow === 0) return;
    this.tryHop(dCol, dRow);
  }

  update(_dt: number): void {
    if (this.phase === 'game-over') return;

    if (this.phase === 'crashed') {
      this.crashFlashLeft -= 1;
      this.updateCars(); // keep traffic moving so the world doesn't freeze
      if (this.crashFlashLeft <= 0) {
        this.lives -= 1;
        if (this.lives <= 0) {
          this.phase = 'game-over';
          this.finalScore = Math.floor(this.score);
          this.finalLevel = this.level;
          this.ctx.events.emit('death', { remainingLives: 0 });
        } else {
          // Reset player to start sidewalk, keep traffic running.
          this.playerCol = Math.floor(COLS / 2);
          this.playerRow = START_ROW;
          this.bestRow = START_ROW;
          this.hopCooldown = HOP_COOLDOWN_TICKS;
          this.phase = 'playing';
          this.ctx.events.emit('death', { remainingLives: this.lives });
        }
      }
      return;
    }

    if (this.phase === 'level-up') {
      this.levelFlashLeft -= 1;
      this.updateCars();
      if (this.levelFlashLeft <= 0) {
        // Reset player to bottom, ramp difficulty.
        this.playerCol = Math.floor(COLS / 2);
        this.playerRow = START_ROW;
        this.bestRow = START_ROW;
        this.level = Math.min(MAX_LEVEL, this.level + 1);
        this.hopCooldown = HOP_COOLDOWN_TICKS;
        this.phase = 'playing';
      }
      return;
    }

    if (this.hopCooldown > 0) this.hopCooldown -= 1;
    this.updateCars();
    this.checkCollision();
  }

  render(g: CanvasRenderingContext2D): void {
    // Clear with sidewalk as default backdrop, then paint road bands on top.
    g.fillStyle = SIDEWALK_DARK;
    g.fillRect(0, 0, WIDTH, HEIGHT);

    this.renderHUD(g);
    this.renderBands(g);
    this.renderCars(g);
    this.renderPlayer(g);

    if (this.phase === 'crashed' && this.crashFlashLeft > 0) {
      const a = this.crashFlashLeft / FLASH_TICKS;
      g.globalAlpha = a * 0.55;
      g.fillStyle = CRASH_FLASH;
      g.fillRect(0, PLAYFIELD_TOP, WIDTH, PLAYFIELD_H);
      g.globalAlpha = 1;
    }
    if (this.phase === 'level-up' && this.levelFlashLeft > 0) {
      const a = this.levelFlashLeft / LEVEL_FLASH_TICKS;
      g.globalAlpha = a * 0.45;
      g.fillStyle = LEVEL_UP_FLASH;
      g.fillRect(0, PLAYFIELD_TOP, WIDTH, PLAYFIELD_H);
      g.globalAlpha = 1;
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      g.font = 'bold 28px ui-monospace, monospace';
      g.fillStyle = HUD_LEVEL;
      g.fillText(`LEVEL ${this.level + 1}`, WIDTH / 2, HEIGHT / 2);
      g.textAlign = 'left';
      g.textBaseline = 'top';
    }

    if (this.phase === 'game-over') this.renderGameOver(g);
  }

  resize(_w: number, _h: number): void {
    /* 480x270 internal — cabinet scales */
  }

  pause(): void {
    /* no audio loops to suspend */
  }

  resume(): void {
    /* no-op */
  }

  teardown(): void {
    this.cars.clear();
    this.laneSpawnTimers.clear();
  }

  getScore(): number {
    return Math.floor(this.score);
  }

  getFinalScore(): number {
    return this.phase === 'game-over' ? this.finalScore : Math.floor(this.score);
  }

  isGameOver(): boolean {
    return this.phase === 'game-over';
  }

  shouldRestart(): boolean {
    return this.phase === 'game-over' && this.restartArmed;
  }

  // --------------------------------------------------------------------------
  // Internals
  // --------------------------------------------------------------------------

  private initLanes(): void {
    for (let row = 0; row < ROWS; row++) {
      if (BAND_LAYOUT[row] === 'road') {
        this.cars.set(row, []);
        // First spawn timer is short so the playfield isn't empty for 3s
        // at game start.
        this.laneSpawnTimers.set(row, 30 + this.ctx.random.int(0, 60));
      }
    }
  }

  private tryHop(dCol: number, dRow: number): void {
    const nextCol = this.playerCol + dCol;
    const nextRow = this.playerRow + dRow;
    // Clamp to playfield bounds — hops into walls cost no time.
    if (nextCol < 0 || nextCol >= COLS) return;
    if (nextRow < 0 || nextRow >= ROWS) return;
    this.playerCol = nextCol;
    this.playerRow = nextRow;
    this.hopCooldown = HOP_COOLDOWN_TICKS;
    this.ctx.audio.sfx('hop');

    // Score for advancing rows (lower row index = closer to goal).
    if (nextRow < this.bestRow) {
      const advanced = this.bestRow - nextRow;
      this.bestRow = nextRow;
      const delta = advanced * SCORE_PER_ROW;
      this.score += delta;
      this.ctx.events.emit('score', { score: Math.floor(this.score), delta });
    }

    if (nextRow === GOAL_ROW) {
      this.score += SCORE_PER_LEVEL;
      this.ctx.audio.sfx('level-up');
      this.ctx.events.emit('levelComplete', { level: this.level, bonus: SCORE_PER_LEVEL });
      this.phase = 'level-up';
      this.levelFlashLeft = LEVEL_FLASH_TICKS;
    }
  }

  private updateCars(): void {
    const levelMult = 1 + (this.level - 1) * CAR_LEVEL_RAMP;
    for (const [row, lane] of this.cars) {
      // Advance + cull off-screen cars.
      const kept: Car[] = [];
      for (const c of lane) {
        c.x += c.vx;
        // Despawn when fully off the far edge (in the car's direction).
        if (c.vx > 0 && c.x > WIDTH + 40) continue;
        if (c.vx < 0 && c.x + c.width < -40) continue;
        kept.push(c);
      }
      this.cars.set(row, kept);

      // Spawn timer.
      let timer = this.laneSpawnTimers.get(row) ?? 0;
      timer -= 1;
      if (timer <= 0) {
        this.spawnCar(row, levelMult);
        const min = Math.floor(SPAWN_TIMER_MIN / levelMult);
        const max = Math.floor(SPAWN_TIMER_MAX / levelMult);
        timer = min + this.ctx.random.int(0, max - min);
      }
      this.laneSpawnTimers.set(row, timer);
    }
  }

  private spawnCar(row: number, levelMult: number): void {
    const dir = laneDirection(row);
    const kind = CAR_KINDS[this.ctx.random.int(0, CAR_KINDS.length - 1)] as CarKind;
    const width = CAR_W * kind.widthMult;
    const baseSpeed = CAR_BASE_SPEED + this.ctx.random.next() * CAR_SPEED_VAR;
    const speed = baseSpeed * levelMult;
    const x = dir === 1 ? -width - 4 : WIDTH + 4;
    const lane = this.cars.get(row);
    if (!lane) return;
    lane.push({
      x,
      row,
      vx: speed * dir,
      bodyColor: kind.body,
      bodyDark: kind.dark,
      width,
    });
  }

  private checkCollision(): void {
    if (BAND_LAYOUT[this.playerRow] !== 'road') return;
    const lane = this.cars.get(this.playerRow);
    if (!lane) return;
    // Player AABB at sub-pixel inset so a car just clipping the tile edge
    // doesn't trigger — gives the player a small "near miss" margin that
    // makes diagonal hops feel skillful instead of unfair.
    const inset = 3;
    const px0 = this.playerCol * TILE + inset;
    const px1 = (this.playerCol + 1) * TILE - inset;
    const py0 = PLAYFIELD_TOP + this.playerRow * TILE + inset;
    const py1 = PLAYFIELD_TOP + (this.playerRow + 1) * TILE - inset;
    for (const c of lane) {
      const cy0 = PLAYFIELD_TOP + c.row * TILE + 2;
      const cy1 = PLAYFIELD_TOP + (c.row + 1) * TILE - 2;
      if (c.x < px1 && c.x + c.width > px0 && cy0 < py1 && cy1 > py0) {
        this.phase = 'crashed';
        this.crashFlashLeft = FLASH_TICKS;
        this.ctx.audio.sfx('crash');
        this.ctx.audio.sfx('life-lost');
        return;
      }
    }
  }

  // --------------------------------------------------------------------------
  // Render helpers
  // --------------------------------------------------------------------------

  private renderBands(g: CanvasRenderingContext2D): void {
    for (let row = 0; row < ROWS; row++) {
      const y = PLAYFIELD_TOP + row * TILE;
      if (BAND_LAYOUT[row] === 'road') {
        // Road surface — alternate two tones per row for subtle depth.
        g.fillStyle = row % 2 === 0 ? ROAD_DARK : ROAD_LIGHT;
        g.fillRect(0, y, WIDTH, TILE);
        // Dashed lane line at the top edge of each road tile (except the
        // first road row in each group, where the line would touch the
        // sidewalk above — looks cleaner without it).
        if (row > 0 && BAND_LAYOUT[row - 1] === 'road') {
          g.fillStyle = ROAD_LINE;
          for (let dashX = 4; dashX < WIDTH; dashX += 16) {
            g.fillRect(dashX, y - 1, 8, 2);
          }
        }
      } else {
        // Sidewalk — concrete tiles. Tile grid lines hint at the grid the
        // player snaps to.
        g.fillStyle = SIDEWALK_LIGHT;
        g.fillRect(0, y, WIDTH, TILE);
        g.fillStyle = SIDEWALK_DARK;
        // Subtle slab seam every 2 tiles.
        for (let col = 0; col < COLS; col += 2) {
          g.fillRect(col * TILE, y + TILE - 2, TILE, 2);
        }
        // Diagonal corner marker — the leftmost and rightmost tiles of
        // each non-goal sidewalk band paint a diagonal stripe so the
        // player can read them as "diagonal-hop friendly entry points."
        // (The 8-direction hop already works anywhere; the marker is a
        // visual nudge.)
        if (row !== GOAL_ROW && row !== START_ROW) {
          g.fillStyle = SIDEWALK_DIAGONAL;
          // Left corner — diagonal slash NW-to-SE.
          for (let i = 0; i < TILE; i++) {
            g.fillRect(i, y + i, 2, 2);
          }
          // Right corner — diagonal slash NE-to-SW.
          for (let i = 0; i < TILE; i++) {
            g.fillRect(WIDTH - TILE + (TILE - 1 - i), y + i, 2, 2);
          }
        }
        // Faint horizontal divider so adjacent bands read distinctly.
        g.fillStyle = SIDEWALK_LINE;
        g.fillRect(0, y, WIDTH, 1);
      }
    }
    // Road edge line at the very top + bottom of each road group.
    for (let row = 0; row < ROWS; row++) {
      const isRoad = BAND_LAYOUT[row] === 'road';
      const aboveIsRoad = row > 0 && BAND_LAYOUT[row - 1] === 'road';
      const belowIsRoad = row < ROWS - 1 && BAND_LAYOUT[row + 1] === 'road';
      if (isRoad && !aboveIsRoad) {
        g.fillStyle = ROAD_EDGE;
        g.fillRect(0, PLAYFIELD_TOP + row * TILE, WIDTH, 2);
      }
      if (isRoad && !belowIsRoad) {
        g.fillStyle = ROAD_EDGE;
        g.fillRect(0, PLAYFIELD_TOP + (row + 1) * TILE - 2, WIDTH, 2);
      }
    }
  }

  private renderCars(g: CanvasRenderingContext2D): void {
    for (const [row, lane] of this.cars) {
      const y = PLAYFIELD_TOP + row * TILE + 2;
      for (const c of lane) {
        // Body shadow.
        g.fillStyle = '#000';
        g.fillRect(c.x + 1, y + 1, c.width, CAR_H);
        // Body.
        g.fillStyle = c.bodyColor;
        g.fillRect(c.x, y, c.width, CAR_H);
        // Dark roof stripe.
        g.fillStyle = c.bodyDark;
        g.fillRect(c.x + 2, y + CAR_H - 5, c.width - 4, 3);
        // Windshield (faces direction of travel).
        g.fillStyle = CAR_WINDOW;
        if (c.vx > 0) {
          g.fillRect(c.x + c.width - 6, y + 3, 4, CAR_H - 6);
        } else {
          g.fillRect(c.x + 2, y + 3, 4, CAR_H - 6);
        }
        // Headlights (forward-facing pair).
        g.fillStyle = CAR_HEADLIGHT;
        if (c.vx > 0) {
          g.fillRect(c.x + c.width - 2, y + 2, 2, 3);
          g.fillRect(c.x + c.width - 2, y + CAR_H - 5, 2, 3);
        } else {
          g.fillRect(c.x, y + 2, 2, 3);
          g.fillRect(c.x, y + CAR_H - 5, 2, 3);
        }
      }
    }
  }

  private renderPlayer(g: CanvasRenderingContext2D): void {
    const x = this.playerCol * TILE;
    const y = PLAYFIELD_TOP + this.playerRow * TILE;
    const cx = x + TILE / 2;
    const cy = y + TILE / 2;
    // Body shadow.
    g.fillStyle = '#000';
    g.fillRect(x + 3, y + 5, 14, 12);
    // Limbs (cross shape).
    g.fillStyle = PLAYER_LIMB;
    g.fillRect(cx - 7, cy - 1, 14, 3); // arms
    g.fillRect(cx - 1, cy + 4, 3, 5);  // legs
    // Body — flashes red briefly during crash.
    const flashing = this.phase === 'crashed' && this.crashFlashLeft % 4 < 2;
    g.fillStyle = flashing ? PLAYER_HIT : PLAYER_BODY;
    g.fillRect(cx - 4, cy - 4, 8, 8);
    // Head.
    g.fillStyle = PLAYER_HEAD;
    g.fillRect(cx - 3, cy - 8, 6, 5);
  }

  private renderHUD(g: CanvasRenderingContext2D): void {
    g.fillStyle = ROAD_DARK;
    g.fillRect(0, 0, WIDTH, HUD_H);
    g.fillStyle = ROAD_EDGE;
    g.fillRect(0, HUD_H - 2, WIDTH, 2);

    g.font = '10px ui-monospace, monospace';
    g.textBaseline = 'top';

    g.textAlign = 'left';
    g.fillStyle = HUD_TEXT;
    g.fillText('SCORE', 8, 4);
    g.fillStyle = HUD_SCORE;
    g.fillText(String(Math.floor(this.score)).padStart(6, '0'), 8, 16);

    g.textAlign = 'center';
    g.fillStyle = HUD_TEXT;
    g.fillText('LEVEL', WIDTH / 2, 4);
    g.fillStyle = HUD_LEVEL;
    g.fillText(String(this.level).padStart(2, '0'), WIDTH / 2, 16);

    g.textAlign = 'right';
    g.fillStyle = HUD_TEXT;
    g.fillText('LIVES', WIDTH - 8, 4);
    // Render lives as a row of icons next to the count.
    g.fillStyle = HUD_LIVES;
    for (let i = 0; i < this.lives; i++) {
      g.fillRect(WIDTH - 16 - i * 10, 18, 6, 8);
    }

    g.textAlign = 'left';
  }

  private renderGameOver(g: CanvasRenderingContext2D): void {
    g.fillStyle = 'rgba(0,0,0,0.72)';
    g.fillRect(0, 0, WIDTH, HEIGHT);

    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.font = '24px ui-monospace, monospace';
    g.fillStyle = CRASH_FLASH;
    g.fillText('GAME OVER', WIDTH / 2, HEIGHT / 2 - 30);

    g.font = '12px ui-monospace, monospace';
    g.fillStyle = HUD_SCORE;
    g.fillText(`SCORE ${String(this.finalScore).padStart(6, '0')}`, WIDTH / 2, HEIGHT / 2);
    g.fillStyle = HUD_LEVEL;
    g.fillText(`LEVEL ${this.finalLevel}`, WIDTH / 2, HEIGHT / 2 + 16);

    g.font = '10px ui-monospace, monospace';
    g.fillStyle = HUD_TEXT;
    g.fillText('PRESS Z TO RETRY', WIDTH / 2, HEIGHT / 2 + 50);
  }
}
